import type { pagine as it } from "../it/pagine.ts";
import { formati } from "../formati.ts";

const f = formati("en");

/** Pagine semplici: elenco stack, porte, registro, utenti, importazione. */
export const pagine: typeof it = {
  "stacks.titolo": "Stacks",
  "stacks.intro": "Saved configurations and their runtime status.",
  "stacks.importa": "Import",
  "stacks.nuovo": "New stack",
  "stacks.blueGreen": "blue/green",
  "stacks.ricreazione": "recreate",
  "stacks.aggiornato": (p: { quando: string }) => `updated ${p.quando}`,
  "stacks.nonAvviato": "never started",
  "stacks.fermo": "stopped",
  "stacks.attivo": "running",
  "stacks.daApplicare": "pending",
  "stacks.errore": "error",
  "stacks.cerca": "Search stacks",
  "stacks.tuttiStati": "All statuses",
  "stacks.filtroStato": "Filter by status",
  "stacks.colStack": "Stack",
  "stacks.colStato": "Status",
  "stacks.colStrategia": "Strategy",
  "stacks.colDominio": "Domain",
  "stacks.colContainer": "Containers",
  "stacks.colTag": "Tag",
  "stacks.tagTitolo": (p: { stack: string }) => `Change the tag of ${p.stack}`,
  "stacks.tagTesto": (p: { variabile: string }) =>
    `A new revision is saved with ${p.variabile} updated and the stack is deployed right away.`,
  "stacks.tagPrecedenti": "Previously deployed",
  "stacks.tagInUso": "in use",
  "stacks.tagConferma": (p: { tag: string }) => `Deploy ${p.tag}`,
  "stacks.modificaTag": "Change the tag and deploy",
  "stacks.tagAggiornato": (p: { tag: string }) => `Deployed with tag ${p.tag}`,
  "stacks.tagSenzaEsito": "The deploy stopped without a result",
  "stacks.colRilascio": "Last deploy",
  "stacks.revisione": (p: { n: number }) => `rev. ${p.n}`,
  "stacks.nessunoCorrisponde": "No stacks match",
  "stacks.azzeraFiltri": "Clear filters",
  "stacks.vuotoCosaFa": "Portman runs your Docker Compose stacks and keeps their configuration.",
  "stacks.vuotoCosaFa2": "Every change becomes a revision you can deploy, and roll back if needed.",
  "stacks.importaEsistente": "Import an existing stack",
  "stacks.inEsecuzione": "running",

  "porte.titolo": "Ports",
  "porte.intro": "Assignments managed by Portman and ports taken by other containers on the host.",
  "porte.riepilogo": (p: { da: number; a: number; assegnate: string; libere: string }) =>
    `Range ${p.da}–${p.a} · ${p.assegnate} assigned · ${p.libere} free`,
  "porte.comeChiederla": "To get a port, declare it as a variable in the compose, for example",
  "porte.esempio": '"${PORT_WEB}:80"',
  "porte.colPorta": "Port",
  "porte.colStack": "Stack",
  "porte.colVariabile": "Variable",
  "porte.colServizio": "Service",
  "porte.nessuna": "No ports assigned",
  "porte.nessunaAiuto": "Ports are assigned the first time a stack that declares one as a variable starts.",
  "porte.esterne": "Taken outside Portman",
  "porte.esterneAiuto": "Containers started by other tools. Portman avoids these ports when assigning one.",

  "utenti.titolo": "Users",
  "utenti.intro": "Who can access Portman and with which role; every action is recorded in Activity.",
  "utenti.nuovo": "New user",
  "utenti.seiTu": "you",
  "utenti.disattivato": "deactivated",
  "utenti.dal": (p: { quando: string }) => `since ${p.quando}`,
  "utenti.passwordAiuto": "At least 12 characters.",
  "utenti.ruolo": "Role",
  "utenti.ruoloAdmin": "manages users and settings",
  "utenti.ruoloOperator": "edits and starts stacks",
  "utenti.ruoloViewer": "read-only",
  "utenti.crea": "Create user",
  "utenti.modificaProfilo": "Edit name and password",
  "utenti.disattiva": "Deactivate",
  "utenti.riattiva": "Reactivate",
  "utenti.disattivaConferma": (p: { nome: string }) =>
    `Deactivate ${p.nome}? Their sessions end and they can't sign in until you reactivate them.`,

  "profilo.titolo": "Profile",
  "profilo.intro": "How you appear in Portman and the password you sign in with.",
  "profilo.nome": "Display name",
  "profilo.nomeAiuto": (p: { username: string }) => `If empty, your username is shown: ${p.username}.`,
  "profilo.salvaNome": "Save name",
  "profilo.nomeSalvato": "Name saved.",
  "profilo.password": "Change password",
  "profilo.passwordIntro": "After the change all your sessions end, this one included: sign in again with the new password.",
  "profilo.passwordAttuale": "Current password",
  "profilo.passwordNuova": "New password",
  "profilo.passwordConferma": "Repeat new password",
  "profilo.passwordDiverse": "The new passwords don't match.",
  "profilo.cambiaPassword": "Change password",

  "import.titolo": "Import an existing stack",
  "import.intro":
    "Portman rebuilds the configuration from the containers on the host: images, variables, ports, volumes and networks. " +
    "Useful to bring in a stack started with other tools, without rewriting it.",
  "import.vuoto": "No Compose projects on the host",
  "import.vuotoAiuto": "Containers started with Docker Compose are detected, even when stopped.",
  "import.attivi": (p: { attivi: number; totale: number }) => `${f.numero(p.attivi)}/${f.numero(p.totale)} running`,
  "import.giaGestito": "already in Portman",
  "import.esamina": "Inspect",
  "import.ricostruita": (p: { progetto: string }) => `Configuration rebuilt from "${p.progetto}"`,
  "import.note": "Keep in mind",
  "import.composeRicostruito": "Rebuilt docker-compose.yml",
  "import.titoloEditor": (p: { progetto: string }) => `${p.progetto} · rebuilt compose`,
  "import.sensibili":
    "Sensitive values were pulled out of the compose: here they're encrypted, while in the compose they'd stay in plain text in the revision history.",
  "import.nome": "Stack name in Portman",
  "import.importa": "Import",
  "import.dopo": "The stack is saved but not started: the containers keep running as they are now.",
};
